import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { PageEditor } from '../PageEditor'
import { KNOWLEDGE_KINDS, type KnowledgeKind, type KnowledgeNote, type NoteInput } from '../../hooks/useKnowledge'

interface NoteFormProps {
    /** The note being edited; none = a new note. */
    note?: KnowledgeNote
    /** An asset note can be limited to the asset itself; an organization note applies everywhere. */
    withSubtreeOption: boolean
    onSubmit: (input: NoteInput & { title: string }) => Promise<void>
    onCancel: () => void
    testIdPrefix: string
}

/**
 * Title, kind and body of a knowledge note (spec 2026-10-10-06). The body is
 * the existing rich-text editor; its Save button submits the whole form.
 */
export function NoteForm({ note, withSubtreeOption, onSubmit, onCancel, testIdPrefix }: NoteFormProps) {
    const { t } = useTranslation('knowledge')
    const [title, setTitle] = useState(note?.title ?? '')
    const [kind, setKind] = useState<KnowledgeKind>(note?.kind ?? 'note')
    const [appliesToSubtree, setAppliesToSubtree] = useState(note?.applies_to_subtree ?? true)
    const [error, setError] = useState<string | null>(null)

    return (
        <div className="space-y-3 rounded-md border border-border bg-card p-3" data-testid={`${testIdPrefix}-form`}>
            <div className="space-y-1">
                <Label htmlFor={`${testIdPrefix}-title`}>{t('title_label')}</Label>
                <Input
                    id={`${testIdPrefix}-title`}
                    value={title}
                    maxLength={200}
                    placeholder={t('title_placeholder')}
                    onChange={(e) => setTitle(e.target.value)}
                    data-testid={`${testIdPrefix}-title-input`}
                />
            </div>
            <div className="flex flex-wrap items-end gap-4">
                <div className="space-y-1">
                    <Label>{t('kind_label')}</Label>
                    <Select value={kind} onValueChange={(v) => setKind(v as KnowledgeKind)}>
                        <SelectTrigger className="w-44" data-testid={`${testIdPrefix}-kind-select`}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {KNOWLEDGE_KINDS.map((k) => (
                                <SelectItem key={k} value={k} data-testid={`${testIdPrefix}-kind-option-${k}`}>
                                    {t(`kind.${k}`)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                {withSubtreeOption && (
                    <label className="flex items-center gap-2 pb-2 text-sm" htmlFor={`${testIdPrefix}-subtree`}>
                        <Checkbox
                            id={`${testIdPrefix}-subtree`}
                            checked={appliesToSubtree}
                            onCheckedChange={setAppliesToSubtree}
                            data-testid={`${testIdPrefix}-subtree-checkbox`}
                        />
                        {t('applies_to_subtree')}
                    </label>
                )}
            </div>
            <PageEditor
                entityType="asset"
                entityUid=""
                initialContent={note?.content}
                showFloatingSaveButton={false}
                showInlineSaveButton
                testIdPrefix={testIdPrefix}
                onSubmit={async ({ content, text_content }) => {
                    if (!title.trim()) {
                        setError(t('title_required'))
                        throw new Error('title required')
                    }
                    setError(null)
                    try {
                        await onSubmit({
                            title: title.trim(),
                            kind,
                            applies_to_subtree: withSubtreeOption ? appliesToSubtree : undefined,
                            content,
                            text_content,
                        })
                    } catch (e) {
                        setError(e instanceof Error ? e.message : t('save_failed'))
                        throw e
                    }
                }}
            />
            {error && (
                <p className="text-sm text-destructive" role="alert" data-testid={`${testIdPrefix}-error`}>
                    {error}
                </p>
            )}
            <div className="flex justify-start">
                <Button type="button" variant="ghost" size="sm" onClick={onCancel} data-testid={`${testIdPrefix}-cancel`}>
                    {t('cancel')}
                </Button>
            </div>
        </div>
    )
}
